import { useAuthenticator } from '@aws-amplify/ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef } from 'react';
import ConfirmResetPasswordScreen from '@/auth/screens/ConfirmResetPasswordScreen';
import ConfirmSignInScreen from '@/auth/screens/ConfirmSignInScreen';
import ForceNewPasswordScreen from '@/auth/screens/ForceNewPasswordScreen';
import ForgotPasswordScreen from '@/auth/screens/ForgotPasswordScreen';
import SetupTotpScreen from '@/auth/screens/SetupTotpScreen';
import SignInScreen from '@/auth/screens/SignInScreen';
import { LoadingShell } from '@/components/LoadingShell';
import { resetAllStores } from '@/stores';
import AuthShell from './AuthShell';

interface AuthRouteSwitchProps {
  children: ReactNode;
}

function useInitAuthMachine() {
  // Replicates @aws-amplify/ui-react-core's `useAuthenticatorInitMachine`,
  // which `<Authenticator>` calls but `<Authenticator.Provider>` does not.
  // Without it, the xstate machine stays in `setup.initConfig` and never
  // transitions to `signInActor`, so navigation events like FORGOT_PASSWORD
  // are silently dropped.
  const { route, initializeMachine } = useAuthenticator((c) => [c.route]);
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current && route === 'setup') {
      initializeMachine({});
      initialized.current = true;
    }
  }, [route, initializeMachine]);
}

/**
 * Drop every trace of the outgoing session once it has actually ended.
 *
 * This lives here, keyed off `authStatus`, rather than in the sign-out button's own
 * handler. Amplify's `signOut()` only dispatches into the auth state machine, so a
 * `queryClient.clear()` next to it runs while the app shell is still mounted and still
 * holds valid tokens: React Query answers a cleared cache by refetching every active
 * query, those requests go out as the *departing* user, and their responses land back
 * in the cache. The clear then leaves the cache full of the previous user's data rather
 * than empty, and the next user is served it — only a hard reload, which drops the
 * in-memory cache outright, appeared to fix it.
 *
 * By the time this effect runs, `children` (the whole authenticated tree) has already
 * unmounted in the same commit, so there are no observers left to refetch and nothing
 * is written back. It also covers the silent path the button never reached — an expired
 * or externally revoked session drops to `unauthenticated` without anyone clicking
 * anything, and used to leave the cache intact.
 */
function useResetOnSessionEnd(authStatus: string) {
  const queryClient = useQueryClient();
  const wasAuthenticated = useRef(false);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      wasAuthenticated.current = true;
      return;
    }
    // Only on the authenticated → not transition; `configuring` on a cold start and
    // the sign-in screens themselves must not wipe a cache they never filled.
    if (!wasAuthenticated.current) return;
    wasAuthenticated.current = false;
    resetAllStores();
    queryClient.clear();
  }, [authStatus, queryClient]);
}

export default function AuthRouteSwitch({ children }: AuthRouteSwitchProps) {
  useInitAuthMachine();
  const { authStatus, route } = useAuthenticator((c) => [c.authStatus, c.route]);
  useResetOnSessionEnd(authStatus);

  if (authStatus === 'authenticated') {
    return <>{children}</>;
  }

  if (authStatus === 'configuring') {
    return <LoadingShell />;
  }

  return (
    <AuthShell>
      <ScreenForRoute route={route} />
    </AuthShell>
  );
}

function ScreenForRoute({ route }: { route: string }) {
  switch (route) {
    case 'signIn':
      return <SignInScreen />;
    case 'forgotPassword':
      return <ForgotPasswordScreen />;
    case 'confirmResetPassword':
      return <ConfirmResetPasswordScreen />;
    case 'confirmSignIn':
      return <ConfirmSignInScreen />;
    case 'forceNewPassword':
      return <ForceNewPasswordScreen />;
    case 'setupTotp':
      return <SetupTotpScreen />;
    default:
      // idle / setup / transition / signOut and anything unknown land on sign-in.
      return <SignInScreen />;
  }
}
